import {pointerUtils} from '@wix/document-manager-core'
import {ReportableError} from '@wix/document-manager-utils'
import type {
    CompRef,
    Pointer,
    PossibleViewModes,
    PS,
    VariableConnections,
    VariableData,
    VariablePointer,
    VariantPointer
} from '@wix/document-services-types'
import _ from 'lodash'
import constants from '../constants/constants'
import dataIds from '../dataModel/dataIds'
import dataModel from '../dataModel/dataModel'
import dataSerialization from '../dataModel/dataSerialization'
import hooks from '../hooks/hooks'
import dsUtils from '../utils/utils'
import variants from '../variants/variants'
import variantsUtils from '../variants/variantsUtils'
import variableUtils from './variablesUtils'
import documentModeInfo from '../documentMode/documentModeInfo'
import type {VariablesExtensionAPI} from '@wix/document-manager-extensions/src/extensions/variables/variables'

const {getPointer} = pointerUtils
const {NAMESPACES_SUPPORTING_VARIABLES} = constants
const VARIABLES_NAMESPACE = 'variables' as const

const createVariablesError = (message: string) => {
    return new ReportableError({
        message,
        errorType: 'variablesError'
    })
}

const validateCompPointer = (ps: PS, componentPointer: Pointer) => {
    if (!componentPointer || !ps.dal.isExist(componentPointer)) {
        throw createVariablesError('The component pointer is not passed or it does not exist')
    }
}
const validateCompAndVariablePointers = (ps: PS, componentPointer: Pointer, variablePointer: VariablePointer) => {
    validateCompPointer(ps, componentPointer)
    if (!variablePointer || !ps.dal.isExist(variablePointer)) {
        throw createVariablesError('The variable pointer is not passed or it does not exist')
    }
    const currentVariableIds = variableUtils.getComponentVariablesIds(ps, componentPointer)
    if (!_.includes(currentVariableIds, variablePointer.id)) {
        throw createVariablesError(`The component ${componentPointer.id} didn't define ${variablePointer.id} variable`)
    }
}
const validateVariants = (ps: PS, compPointerWithVariants: VariantPointer) => {
    _.forEach(compPointerWithVariants.variants, variant => {
        if (!ps.dal.isExist(variant)) {
            throw createVariablesError(`Passing component with non-existing variant ${variant.id}`)
        }
    })
}

const isVariableInUse = (ps: PS, variablePointer: VariablePointer) => {
    const references = ps.extensionAPI.relationships.getReferencesToPointer(variablePointer)
    return references.some(({type}) => type !== VARIABLES_NAMESPACE)
}

const validateVariableNotInUse = (ps: PS, variablePointer: VariablePointer) => {
    if (isVariableInUse(ps, variablePointer)) {
        throw createVariablesError(`The variable is in use`)
    }
}

const getVariableToAddRef = (): VariablePointer =>
    pointerUtils.getPointer(dataIds.generateNewId(VARIABLES_NAMESPACE), VARIABLES_NAMESPACE) as VariablePointer

const getMasterPageRef = (ps: PS): CompRef => ps.pointers.components.getMasterPage(documentModeInfo.getViewMode(ps))

const getComponentVariablesList = (ps: PS, componentPointer: Pointer): VariablePointer[] => {
    validateCompPointer(ps, componentPointer)
    return variableUtils
        .getComponentVariablesIds(ps, componentPointer)
        .map((variableId: string) => ps.pointers.getPointer(variableId, VARIABLES_NAMESPACE) as VariablePointer)
}

const getVariable = (ps: PS, componentPointer: CompRef, variablePointer: VariablePointer) => {
    validateCompAndVariablePointers(ps, componentPointer, variablePointer)
    const variableDataItem = ps.dal.get(variablePointer)
    const variableDefaultValueDataItem = variantsUtils.getDataConsideringVariants(
        ps,
        variablePointer,
        'value',
        VARIABLES_NAMESPACE
    )
    const variableValueType = ps.extensionAPI.variables.getVariableValueType(variableDataItem.type)
    return {
        name: variableDataItem.name,
        cssVarName: variableDataItem.cssVarName,
        type: variableDataItem.type,
        value: {type: variableValueType, value: variableDefaultValueDataItem.value}
    }
}

const getGlobalVariable = (ps: PS, variablePointer: VariablePointer): VariableData =>
    getVariable(ps, getMasterPageRef(ps), variablePointer)

const updateVariableValueConsideringVariants = (
    ps: PS,
    variableToAddRef: VariantPointer,
    value: VariableData['value']
) => {
    variantsUtils.updateDataConsideringVariants(ps, variableToAddRef, 'value', value, VARIABLES_NAMESPACE)
    if (!variableToAddRef.variants) {
        const variableData = ps.dal.get(variableToAddRef)
        if (variableData.cssVarName) {
            let newValue = value.value
            if (ps.extensionAPI.variables.isCssVariableString(newValue)) {
                const cssVarName = ps.extensionAPI.variables.extractCssVarName(newValue)
                newValue = ps.extensionAPI.global.variables.getVariableDataByCssVarName(cssVarName)?.value
            }

            hooks.executeHook(hooks.HOOKS.GLOBAL_VARIABLES.WIX_THEME_VAR_WAS_CHANGED, '', [
                ps,
                {
                    variablePointer: variableToAddRef,
                    variableType: variableData.type,
                    value: newValue
                }
            ])
        }
    }
}

const removeVariableConsideringVariants = (ps: PS, variablePointer: VariantPointer) => {
    const variableData = ps.dal.get(variablePointer)
    variantsUtils.removeDataConsideringVariants(ps, variablePointer, 'value', VARIABLES_NAMESPACE)
    if (!variablePointer.variants) {
        if (variableData.cssVarName) {
            hooks.executeHook(hooks.HOOKS.GLOBAL_VARIABLES.WIX_THEME_VAR_WAS_CHANGED, '', [
                ps,
                {
                    variablePointer,
                    variableType: variableData.type,
                    value: null
                }
            ])
        }
    }
}

const addVariable = (
    ps: PS,
    variableToAddRef: VariablePointer,
    componentPointer: Pointer,
    variableData: VariableData,
    cssVarName?: string
) => {
    validateCompPointer(ps, componentPointer)
    if (ps.dal.isExist(variableToAddRef)) {
        throw createVariablesError('Variable with the same id already exists')
    }
    if (!variableData) {
        throw createVariablesError('The variableData is missing')
    }
    if (!variableData.type) {
        throw createVariablesError('The variableData is missing variable type')
    }
    if (!variableData.value) {
        throw createVariablesError('The variableData is missing default value for the variant')
    }
    const {variables} = ps.extensionAPI as VariablesExtensionAPI
    const cssVarNameToUse = cssVarName ?? variableData.cssVarName
    if (!!cssVarNameToUse && variables.isDuplicateCssVarName(variableToAddRef, cssVarNameToUse, componentPointer)) {
        throw createVariablesError('Variable with the same cssVarName already exists')
    }

    const pagePointer = ps.pointers.components.getPageOfComponent(componentPointer)
    const pageId = pagePointer?.id

    variableData.value = ps.extensionAPI.variables.updateCssVariableConnections(variableData.value)

    //create variable
    const variableID = dataSerialization.addSerializedItemToPage(
        ps,
        pageId,
        {
            name: variableData.name,
            cssVarName: cssVarNameToUse,
            type: variableData.type
        },
        variableToAddRef.id,
        VARIABLES_NAMESPACE
    )
    //add default value
    updateVariableValueConsideringVariants(ps, variableToAddRef, variableData.value)

    const variablesListPointer = dataModel.getComponentDataPointerByType(ps, componentPointer, VARIABLES_NAMESPACE)
    const doesVariablesListExists = ps.dal.isExist(variablesListPointer)
    if (!doesVariablesListExists) {
        //create variables list and link comp to it
        const variablesListID = dataModel.addDeserializedItemToPage(ps, pageId, VARIABLES_NAMESPACE, {
            type: 'VariablesList',
            variables: [`#${variableID}`]
        })
        dataModel.linkComponentToItemByType(ps, componentPointer, variablesListID, VARIABLES_NAMESPACE)
    } else {
        //add variable to existing variables list of the component
        const variablesListDataItem = ps.dal.get(variablesListPointer)
        ps.dal.set(variablesListPointer, {
            ...variablesListDataItem,
            variables: variablesListDataItem.variables.concat([`#${variableID}`])
        })
    }
}

const addGlobalVariable = (
    ps: PS,
    variableToAddRef: VariablePointer,
    variableData: VariableData,
    cssVarName?: string
): void => {
    const masterPageComp = getMasterPageRef(ps)
    const variablesListPointer = dataModel.getComponentDataPointerByType(ps, masterPageComp, VARIABLES_NAMESPACE)
    if (!ps.dal.isExist(variablesListPointer)) {
        const masterPageVariablesQuery = `variables-${constants.MASTER_PAGE_ID}`
        dataModel.addDeserializedItemToPage(
            ps,
            constants.MASTER_PAGE_ID,
            VARIABLES_NAMESPACE,
            {
                type: 'VariablesList',
                variables: []
            },
            masterPageVariablesQuery
        )
        dataModel.linkComponentToItemByType(ps, masterPageComp, masterPageVariablesQuery, VARIABLES_NAMESPACE)
    }
    addVariable(ps, variableToAddRef, masterPageComp, variableData, cssVarName)
}

const addVariableWithCustomId = (ps: PS, variableId: string, componentPointer: Pointer, variableData) => {
    return addVariable(
        ps,
        {
            id: variableId,
            type: VARIABLES_NAMESPACE
        },
        componentPointer,
        variableData
    )
}

const removeVariable = (ps: PS, componentPointer: Pointer, variablePointer: VariablePointer) => {
    validateCompAndVariablePointers(ps, componentPointer, variablePointer)
    validateVariableNotInUse(ps, variablePointer)

    //remove all the values of the variable
    removeVariableConsideringVariants(ps, variablePointer)

    //remove variable from variables list
    const variablesListPointer = dataModel.getComponentDataPointerByType(ps, componentPointer, VARIABLES_NAMESPACE)
    const variablesListDataItem = ps.dal.get(variablesListPointer)
    ps.dal.set(variablesListPointer, {
        ...variablesListDataItem,
        variables: _.without(variablesListDataItem.variables, `#${variablePointer.id}`)
    })

    //remove the variable
    ps.dal.remove(variablePointer)
}

const removeGlobalVariable = (ps: PS, variablePointer: VariablePointer): void =>
    removeVariable(ps, getMasterPageRef(ps), variablePointer)

const updateVariableDefinition = (
    ps: PS,
    componentPointer: Pointer,
    variablePointer: VariablePointer,
    variableDefinition: Partial<VariableData>
) => {
    validateCompAndVariablePointers(ps, componentPointer, variablePointer)
    const currentVariable = ps.dal.get(variablePointer)
    if (variableDefinition.type && currentVariable.type !== variableDefinition.type) {
        throw createVariablesError('Variable type cannot be changed')
    }
    const {variables} = ps.extensionAPI as VariablesExtensionAPI
    if (
        variableDefinition.cssVarName &&
        variables.isDuplicateCssVarName(variablePointer, variableDefinition.cssVarName, componentPointer)
    ) {
        throw createVariablesError('Variable with the same cssVarName already exists')
    }

    if (variableDefinition.name || variableDefinition.cssVarName) {
        const previousCssVarName = currentVariable.cssVarName
        ps.dal.set(
            variablePointer,
            _.merge(currentVariable, {name: variableDefinition.name, cssVarName: variableDefinition.cssVarName})
        )
        if (previousCssVarName && variableDefinition.cssVarName !== previousCssVarName) {
            hooks.executeHook(hooks.HOOKS.GLOBAL_VARIABLES.WIX_THEME_VAR_WAS_CHANGED, '', [
                ps,
                {
                    variablePointer,
                    variableType: currentVariable.type,
                    value: null
                }
            ])
        }
    }

    if (variableDefinition.value) {
        variableDefinition.value = ps.extensionAPI.variables.updateCssVariableConnections(variableDefinition.value)
        updateVariableValueConsideringVariants(ps, variablePointer, variableDefinition.value)
    }
}

const updateGlobalVariableDefinition = (
    ps: PS,
    variablePointer: VariablePointer,
    variableDefinition: Partial<VariableData>
): void => updateVariableDefinition(ps, getMasterPageRef(ps), variablePointer, variableDefinition)

const getVariableData = (ps: PS, compPointerWithVariants: CompRef, variablePointer: VariablePointer) => {
    validateCompPointer(ps, compPointerWithVariants)
    const templatePointer = pointerUtils.getRepeatedItemPointerIfNeeded(compPointerWithVariants)
    return ps.extensionAPI.variables.getVariableData(templatePointer, variablePointer)
}

const getGlobalVariableData = (ps: PS, variablePointer: VariablePointer) => {
    return getVariableData(ps, getMasterPageRef(ps), variablePointer)
}

const removeVariableData = (ps: PS, compPointerWithVariants: VariantPointer, variablePointer: VariablePointer) => {
    validateCompAndVariablePointers(ps, compPointerWithVariants, variablePointer)
    if (_.isEmpty(compPointerWithVariants.variants)) {
        throw createVariablesError(
            'Cannot remove the default variable value, please use the components.variables.remove function for that'
        )
    }
    validateVariants(ps, compPointerWithVariants)
    const variablePointerWithVariants = variants.getPointerWithVariants(
        ps,
        variablePointer,
        compPointerWithVariants.variants
    )
    removeVariableConsideringVariants(ps, variablePointerWithVariants)
}

const removeGlobalVariableData = (ps: PS, variablePointer: VariablePointer) => {
    return removeVariableData(ps, getMasterPageRef(ps), variablePointer)
}

const updateVariableData = (
    ps: PS,
    compPointerWithVariants: VariantPointer,
    variablePointer: VariablePointer,
    variableData
) => {
    validateCompAndVariablePointers(ps, compPointerWithVariants, variablePointer)
    validateVariants(ps, compPointerWithVariants)
    const variablePointerWithVariants = compPointerWithVariants.variants
        ? variants.getPointerWithVariants(ps, variablePointer, compPointerWithVariants.variants)
        : variablePointer
    variableData = ps.extensionAPI.variables.updateCssVariableConnections(variableData)
    updateVariableValueConsideringVariants(ps, variablePointerWithVariants, variableData)
}

const updateGlobalVariableData = (ps: PS, variablePointer: VariablePointer, variableData) => {
    return updateVariableData(ps, getMasterPageRef(ps), variablePointer, variableData)
}

const getComponentsUsingVariable = (
    ps: PS,
    variablePointer: VariablePointer,
    viewMode: PossibleViewModes = constants.VIEW_MODES.DESKTOP
) => ps.extensionAPI.variables.getComponentsUsingVariable(variablePointer, viewMode)

function removeOutOfScopeVariables(ps: PS, compPointer: Pointer) {
    const ancestorsVariableIds = variableUtils.collectAncestorsVariablesIds(ps, compPointer)
    removeOutOfScopeVariablesRecursively(ps, compPointer, ancestorsVariableIds)
}

function removeOutOfScopeVariablesRecursively(ps: PS, compPointer: Pointer, scopedVariableIds: string[]) {
    NAMESPACES_SUPPORTING_VARIABLES.forEach(namespace =>
        variableUtils.removeOutOfScopeVariablesFromRefArray(ps, compPointer, namespace, scopedVariableIds)
    )
    const componentChildren = ps.pointers.full.components.getChildren(compPointer)
    scopedVariableIds.push(variableUtils.getComponentVariablesIds(ps, compPointer))
    _.forEach(componentChildren, child => removeOutOfScopeVariablesRecursively(ps, child, scopedVariableIds))
}

const getVariableConnections = (ps: PS, obj) => {
    const {variableConnections} = obj ?? {}
    if (!variableConnections) return {}

    return _.mapValues(variableConnections, variableConnection => {
        if (!_.isString(variableConnection)) {
            throw createVariablesError('Invalid variable connections')
        }

        return {
            id: dsUtils.stripHashIfExists(variableConnection),
            type: 'variables'
        }
    })
}

const getComponentVariableConnections = (ps: PS, componentPointer: CompRef): VariableConnections => {
    const variableConnections = {}
    for (const namespace of NAMESPACES_SUPPORTING_VARIABLES) {
        const dataItemPointer = variantsUtils.getComponentDataPointerConsideringVariants(
            ps,
            componentPointer,
            namespace
        )
        const dataItem = dataItemPointer && ps.dal.get(dataItemPointer)
        const connections = dataItem?.variableConnections
        if (connections) {
            variableConnections[namespace] = {}
            for (const {path, variableId} of connections) {
                variableConnections[namespace][path] = getPointer(variableId, VARIABLES_NAMESPACE)
            }
        }
    }

    return variableConnections
}

const connect = (ps: PS, obj, property: string, variablePointer: VariablePointer) => {
    if (_.isNil(variablePointer) || !ps.dal.isExist(variablePointer)) {
        throw createVariablesError('The variable pointer is not passed or it does not exist')
    }

    if (!_.has(obj, property)) {
        throw createVariablesError('Property does not exist on object')
    }

    if (_.isArray(obj)) {
        return _.set(obj, [property, 'variableConnections', ''], variablePointer.id)
    }

    return _.set(obj, ['variableConnections', property], variablePointer.id)
}

const disconnect = (ps: PS, obj, property: string) => {
    if (_.isArray(obj)) {
        if (_.has(obj, [property, 'variableConnections'])) delete obj[property].variableConnections['']
        return obj
    }

    if (_.has(obj, ['variableConnections', property])) delete obj.variableConnections[property]
    return obj
}
hooks.registerHook(hooks.HOOKS.CHANGE_PARENT.AFTER, removeOutOfScopeVariables)

export default {
    getVariableToAddRef,
    getComponentVariablesList,
    getVariable,
    getGlobalVariable,
    addVariable,
    addGlobalVariable,
    addVariableWithCustomId,
    removeVariable,
    removeGlobalVariable,
    updateVariableDefinition,
    updateGlobalVariableDefinition,
    getVariableData,
    getGlobalVariableData,
    removeVariableData,
    removeGlobalVariableData,
    updateVariableData,
    updateGlobalVariableData,
    getComponentsUsingVariable,
    getVariableConnections,
    getComponentVariableConnections,
    connect,
    disconnect
}
